<template>
  <div ref="container" class="region-chart-container">
    <svg
      ref="svg"
      class="overflow-visible text-gray-500 fill-current"
      :width="width"
      :height="height"
    ></svg>
  </div>
</template>

<script>
import * as d3 from "d3";
import { capitalize } from "../utils/helpers";
export default {
  name: "RegionChart",
  props: {
    data: {
      type: Array,
      default: () => [
        {
          id: 1,
          name: "Val 1",
          min: 2.2,
          max: 3.1,
          value: 3,
        },
        { id: 2, name: "Val 2", min: 4.6, max: 5, value: 4.7 },
        { id: 3, name: "Val 3", min: 2.6, max: 3.4, value: 3 },
      ],
    },
    // max: {
    //   type: Number,
    //   default: null,
    // },
    range: {
      type: Array,
      default: null,
    },
    color: {
      type: String,
      default: "#D1D5DB",
    },
    stroke: {
      type: String,
      default: "#000000",
    },
    duration: {
      type: Number,
      default: 500,
    },
    average: {
      type: Number,
      default: null,
    },
    ease: {
      type: String,
      default: "easeCubic",
    },
    barHeight: {
      type: Number,
      default: 24,
    },
    margin: {
      type: Object,
      default: () => ({ top: 30, right: 0, bottom: 0, left: 112 }),
    },
    colors: {
      type: Object,
      default: () => ({ min: "#DBEAFE", max: "#1E3A8A" }),
    },
    noDataColor: {
      type: String,
      default: "#eee",
    },
    showXAxis: {
      type: Boolean,
      default: true,
    },
    selected: {
      type: [String, Number],
      default: null,
    },
    highlightColor: {
      type: String,
      default: "#f3f4f6",
    },
  },
  data() {
    return {
      width: 500,
      hoverIndex: null,
    };
  },
  computed: {
    // return names as ids
    ids() {
      return this.data.map((d) => d.id);
    },
    // return min and max dates
    dataExtent() {
      if (this.range) return this.range;
      return d3.extent(this.data.flatMap((d) => [d.min, d.max]));
    },
    // scale for x axis (maps date values to width)
    x() {
      return d3
        .scaleLinear()
        .domain(this.dataExtent)
        .range([this.margin.left, this.width - this.margin.right])
        .nice();
    },
    // scale for y axis (positions each item vertically)
    y() {
      return d3
        .scaleBand()
        .domain(this.ids)
        .range([this.height - this.margin.bottom, this.margin.top])
        .padding(0.1);
    },
    height() {
      return (
        this.data.length * this.barHeight + this.margin.top + this.margin.bottom
      );
    },
    colorScale() {
      const interpolator = d3.interpolateRgb(this.colors.min, this.colors.max);
      const scale = d3.scaleLinear().domain(this.dataExtent);
      return d3
        .scaleSequential((d) => interpolator(scale(d)))
        .unknown(this.noDataColor);
    },
  },
  watch: {
    data() {
      this.draw();
    },
    range() {
      this.draw();
    },
    selected() {
      this.highlight();
    },
  },
  mounted() {
    this.setSize();
    this.createChart();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.resize();
      });
      this.resizeObserver.observe(this.$refs.container);
    } else {
      // for IE
      window.addEventListener("resize", this.resize);
    }
  },
  beforeDestroy() {
    if (window.ResizeObserver) {
      this.resizeObserver.unobserve(this.$refs.container);
    } else {
      // for IE
      window.removeEventListener("resize", this.resize);
    }
  },
  methods: {
    setSize() {
      const container = this.$refs.container;
      if (container) {
        this.width = container.clientWidth;
        // this.height = container.clientHeight;
      }
    },
    resize() {
      const currentWidth = this.width;
      const currentHeight = this.height;
      this.setSize();
      if (currentWidth !== this.width || currentHeight !== this.height) {
        this.updateChart();
      }
    },
    draw(delay = this.duration, duration = this.duration) {
      // this.svg.attr("viewBox", [0, 0, this.width, this.height]);

      // BG RECTANGLE SELECTION
      const selectionBg = this.rectsBg
        .selectAll("rect")
        .data(this.data, (d) => `${d.id}-${d.name}`);

      // RECTANGLE SELECTION
      const selection = this.rects
        .selectAll("rect")
        .data(this.data, (d) => `${d.id}-${d.name}`);

      // LINE SELECTION
      const selectionLines = this.lines
        .selectAll("line")
        .data(this.data, (d) => `${d.id}-${d.name}`);

      // calculate delays
      const updating = selection;
      const updateDelay1 = 0;
      const updateDelay = updateDelay1 + updating.size() ? updateDelay1 : 0;
      const enterDelay = updateDelay + (updating.size() ? delay * 2 : delay);
      const enterDelayAxis = updateDelay + (updating.size() ? delay : 0);

      // DRAW GRADIENTS
      this.drawGradients(duration);

      // DRAW AXIS
      this.drawAxis(enterDelayAxis, duration);

      const proportion = 0.7;

      // DRAW BACKGROUND RECTANGLES
      selectionBg.join(
        (enter) =>
          enter
            .append("rect")
            .attr("x", 0)
            .attr("y", (d) => this.y(d.id))
            .attr("height", this.y.step())
            .attr("width", this.width)
            .on("mouseenter", (event, data) =>
              // we will "fake" the event target so it points to the line instead of the whole rectangle
              this.$emit("hover", {
                event: { target: document.getElementById(`line-${data.id}`) },
                data,
              })
            )
            .on("mouseleave", () => this.$emit("hover", null)),
        (update) =>
          update.call((update) =>
            update
              .attr("x", 0)
              .attr("y", (d) => this.y(d.id))
              .attr("height", this.y.step())
              .attr("width", this.width)
          ),
        (exit) => exit.call((exit) => exit.remove())
      );

      // DRAW RECTANGLES
      selection.join(
        (enter) =>
          enter
            .append("rect")
            // .attr("fill", this.color)
            .attr("fill", (d) => `url(#gradient-${d.id})`)
            // .attr("fill", "url(#mainGradient)")
            .attr("x", (d) => this.x(d.value))
            .attr(
              "y",
              (d) => this.y(d.id) + this.y.bandwidth() * ((1 - proportion) / 2)
            )
            .attr("height", this.y.bandwidth() * proportion)
            .attr("width", 0)
            // .on("mouseenter", (event, data) =>
            //   this.$emit("hover", { event, data })
            // )
            // .on("mouseleave", () => this.$emit("hover", null))
            .call((enter) =>
              enter
                .transition()
                .duration(duration)
                .ease(d3[this.ease])
                .delay(enterDelay)
                // .attr("y", (d) => this.y(d.value))
                .attr("x", (d) => this.x(d.min))
                .attr("width", (d) => this.x(d.max) - this.x(d.min))
            ),
        (update) =>
          update.call((update) =>
            update
              .transition()
              .duration(duration)
              .ease(d3[this.ease])
              .delay(updateDelay)
              // first animate the width of the bar
              .attr("x", (d) => this.x(d.min))
              .attr("width", (d) => this.x(d.max) - this.x(d.min))
              // and then move into the new position
              .transition()
              .attr(
                "y",
                (d) =>
                  this.y(d.id) + this.y.bandwidth() * ((1 - proportion) / 2)
              )
              .attr("height", this.y.bandwidth() * proportion)
          ),
        (exit) =>
          exit.call((exit) =>
            exit
              .transition()
              .duration(duration)
              .ease(d3[this.ease])
              .attr("x", (d) => this.x(d.value))
              .attr("width", 0)
              .remove()
          )
      );

      // DRAW LINES
      selectionLines.join(
        (enter) =>
          enter
            .append("line")
            .attr("stroke", this.stroke)
            .attr("stroke-width", 2)
            .attr("x1", (d) => this.x(d.value))
            .attr("x2", (d) => this.x(d.value))
            .attr("y1", (d) => this.y(d.id))
            .attr("y2", (d) => this.y(d.id) + this.y.bandwidth())
            .attr("id", (d) => `line-${d.id}`)
            .attr("opacity", 0)
            .call((enter) =>
              enter
                .transition()
                .duration(duration)
                .ease(d3[this.ease])
                .delay(enterDelay)
                .attr("opacity", 1)
            ),
        (update) =>
          update.call((update) =>
            update
              .transition()
              .duration(duration)
              .ease(d3[this.ease])
              .delay(updateDelay)
              // first animate the x position of the line
              .attr("x1", (d) => this.x(d.value))
              .attr("x2", (d) => this.x(d.value))
              .attr("opacity", 1)
              // and then move into the new position
              .transition()
              .attr("y1", (d) => this.y(d.id))
              .attr("y2", (d) => this.y(d.id) + this.y.bandwidth())
          ),
        (exit) =>
          exit
            .transition()
            .duration(duration)
            .ease(d3[this.ease])
            .attr("opacity", 0)
            .attr("x1", (d) => this.x(d.value))
            .attr("x2", (d) => this.x(d.value))
            .remove()
      );

      // AVERAGE LINE
      this.drawAverageLine(duration);
    },
    drawAxis(delay, duration) {
      if (this.showXAxis) {
        // AXIS
        this.xAxis
          .attr("transform", "translate(0," + this.margin.top + ")")
          .transition()
          .duration(duration)
          .ease(d3[this.ease])
          .delay(delay)
          .call(d3.axisTop(this.x));
      }

      this.yAxis
        .attr("transform", `translate(${this.width},0)`)
        .transition()
        .duration(duration)
        .ease(d3[this.ease])
        .delay(delay)
        .call(
          d3
            .axisLeft(this.y)
            .tickSize(this.width - this.margin.left - this.margin.right)
            .tickFormat((_, i) => capitalize(this.data[i].name))
        );
    },
    drawAverageLine(duration) {
      this.avgLine
        .transition()
        .duration(duration)
        .ease(d3[this.ease])
        .attr("x1", this.x(this.average) || 0)
        .attr("x2", this.x(this.average) || 0)
        .attr("y1", 0)
        .attr("y2", this.height);
    },
    drawGradients(duration) {
      // const mainGradient = this.gradients
      //   .append("linearGradient")
      //   .attr("id", "mainGradient");
      // mainGradient
      //   .append("stop")
      //   .attr("stop-color", this.colorScale(0))
      //   .attr("offset", "0");
      // mainGradient
      //   .append("stop")
      //   .attr("stop-color", this.colorScale(this.dataExtent[1]))
      //   .attr("offset", "1");
      // mask
      // const mask = this.gradients
      //   .append("mask")
      //   .attr("id", "fade")
      //   .attr("maskContentUnits", "objectBoundingBox");
      // <mask id="fade" maskContentUnits="objectBoundingBox">
      //   <rect width="1" height="1" fill="url(#fadeGrad)" />
      // </mask>;
      const selection = this.gradients
        .selectAll("linearGradient")
        .data(this.data, (d) => d.id);

      selection.join(
        (enter) =>
          enter
            .append("linearGradient")
            .attr("id", (d) => `gradient-${d.id}`)
            .call((gradient) => {
              gradient
                .append("stop")
                .attr("stop-color", this.color)
                .attr("stop-opacity", "0")
                .attr("offset", "0");
              gradient
                .append("stop")
                .attr("class", "middle-stop")
                .attr("stop-color", this.color)
                .attr(
                  "offset",
                  (d) => (d.value - d.min) / (d.max - d.min) || 0
                );
              gradient
                .append("stop")
                .attr("stop-color", this.color)
                .attr("stop-opacity", "0")
                .attr("offset", "1");
            }),
        (update) =>
          update.call((update) =>
            update
              .select(".middle-stop")
              .transition()
              .duration(duration)
              .ease(d3[this.ease])
              .attr("offset", (d) => (d.value - d.min) / (d.max - d.min) || 0)
          ),
        (exit) =>
          exit
            .transition()
            .duration(duration)
            .ease(d3[this.ease])
            .attr("opacity", 0)
            .remove()
      );
    },
    highlight() {
      if (this.selected === null) {
        this.highlightRect.attr("opacity", 0);
      } else {
        this.highlightRect
          .attr("opacity", 1)
          .attr("width", this.width)
          .attr("height", this.y.bandwidth())
          .attr("y", this.y(this.selected));
      }
    },
    updateChart() {
      this.draw(0, 0);
    },
    createChart() {
      if (!this.svg) {
        this.svg = d3.select(this.$refs.svg);

        this.highlightRect = this.svg
          .append("rect")
          .attr("fill", this.highlightColor)
          .attr("class", "pointer-events-none");

        this.gradients = this.svg.append("defs");

        this.yAxis = this.svg
          .append("g")
          .attr("class", "y-axis")
          .attr("transform", `translate(${this.width},0)`);

        this.rects = this.svg.append("g").attr("class", "rects");
        this.lines = this.svg.append("g").attr("class", "lines");

        // for pointer events - mouse hover emit
        this.rectsBg = this.svg.append("g").attr("opacity", 0);

        this.xAxis = this.svg
          .append("g")
          .attr("class", "x-axis")
          .attr(
            "transform",
            "translate(0," + (this.height - this.margin.bottom) + ")"
          );

        // average line
        this.avgLine = this.svg
          .append("line")
          .attr("stroke", this.stroke)
          .attr("stroke-opacity", 0.7)
          .attr("stroke-dasharray", "4, 4")
          .attr("x1", 0)
          .attr("x2", 0)
          .attr("y1", 0)
          .attr("y2", this.height);

        //first paint with no delay
        this.draw(0);
      }
    },
  },
};
</script>

<style>
.region-chart-container .y-axis .tick line {
  stroke-opacity: 0.2;
  stroke-dasharray: 4, 4;
  /* display: none; */
}
.region-chart-container .y-axis .domain {
  display: none;
}

.region-chart-container .y-axis .tick text {
  @apply text-xs font-medium;
}

.region-chart-container .x-axis .domain {
  display: none;
}
</style>
